import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Steps, Card, Table, Tag, Button, Space, Typography, Spin, Alert, Upload,
  Form, Input, InputNumber, Select, Checkbox, Rate, Descriptions, message,
  Modal, Empty, Row, Col, Statistic, Divider, Tooltip
} from 'antd';
import {
  FileSearchOutlined, UploadOutlined, PlusOutlined, DeleteOutlined,
  ThunderboltOutlined, CheckCircleOutlined, CloseCircleOutlined,
  DownloadOutlined, StarOutlined, TrophyOutlined, BulbOutlined,
  CheckOutlined, CloseOutlined, ReloadOutlined,
  LeftOutlined, RightOutlined, NumberOutlined
} from '@ant-design/icons';
import { rfpService, clientVendorService, userService } from '../services/api';
import '../styles/Phase5.css';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;
const { Option } = Select;
const { Dragger } = Upload;

const STATUS_TO_STEP = {
  draft: 0,
  requirements_review: 1,
  scoring: 2,
  evaluation: 4,
  decision: 5,
  closed: 5,
};

const STEP_TO_STATUS = {
  0: 'draft',
  1: 'requirements_review',
  2: 'scoring',
  3: 'scoring',
  4: 'evaluation',
  5: 'decision',
};

const PRIORITY_COLORS = {
  low: 'default',
  medium: 'blue',
  high: 'orange',
  critical: 'red',
};

const RFPProjectDetail = () => {
  const { id: projectId } = useParams();
  const navigate = useNavigate();

  // Core project state
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentStep, setCurrentStep] = useState(0);

  // Step 0: Setup
  const [setupForm] = Form.useForm();
  const [brdFile, setBrdFile] = useState(null);
  const [extracting, setExtracting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [users, setUsers] = useState([]);

  // Step 1: Requirements
  const [requirements, setRequirements] = useState([]);
  const [extractedRequirements, setExtractedRequirements] = useState([]);
  const [loadingRequirements, setLoadingRequirements] = useState(false);
  const [savingRequirements, setSavingRequirements] = useState(false);
  const [addReqModalVisible, setAddReqModalVisible] = useState(false);
  const [addReqForm] = Form.useForm();

  // Step 2: Vendors
  const [candidates, setCandidates] = useState([]);
  const [clientVendors, setClientVendors] = useState([]);
  const [loadingCandidates, setLoadingCandidates] = useState(false);
  const [addVendorModalVisible, setAddVendorModalVisible] = useState(false);
  const [addExternalModalVisible, setAddExternalModalVisible] = useState(false);
  const [vendorForm] = Form.useForm();
  const [externalForm] = Form.useForm();
  const [autoMatching, setAutoMatching] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [dismissedSuggestions, setDismissedSuggestions] = useState(new Set());

  // Step 3: Scoring
  const [scoringMatrix, setScoringMatrix] = useState(null);
  const [loadingScoring, setLoadingScoring] = useState(false);
  const [scores, setScores] = useState({});
  const [scoringInputMode, setScoringInputMode] = useState('number'); // 'number' or 'stars'

  // Step 4: Results
  const [results, setResults] = useState(null);
  const [loadingResults, setLoadingResults] = useState(false);

  // Step 5: Decision
  const [selectedWinner, setSelectedWinner] = useState(null);
  const [rationale, setRationale] = useState('');
  const [closing, setClosing] = useState(false);

  // -------------------------------------------------------------------
  // Data fetching
  // -------------------------------------------------------------------

  const initialStepSet = React.useRef(false);

  const fetchProject = useCallback(async () => {
    try {
      setLoading(true);
      const data = await rfpService.getProject(projectId);
      setProject(data);

      // Only set step from status on initial load
      if (!initialStepSet.current) {
        const step = STATUS_TO_STEP[data.status] !== undefined ? STATUS_TO_STEP[data.status] : 0;
        setCurrentStep(step);
        initialStepSet.current = true;
      }

      // Pre-fill setup form
      setupForm.setFieldsValue({
        title: data.title,
        description: data.description,
        rfp_type: data.rfp_type,
        deadline: data.deadline,
        owner_user_id: data.owner_user_id,
      });

      if (data.selected_candidate_id) {
        setSelectedWinner(data.selected_candidate_id);
      }
      if (data.decision_rationale) {
        setRationale(data.decision_rationale);
      }
    } catch (error) {
      console.error('Error fetching project:', error);
      message.error('Failed to load RFP project');
    } finally {
      setLoading(false);
    }
  }, [projectId, setupForm]);

  const fetchRequirements = useCallback(async () => {
    try {
      setLoadingRequirements(true);
      const data = await rfpService.getRequirements(projectId);
      setRequirements(data);
    } catch (error) {
      console.error('Error fetching requirements:', error);
      message.error('Failed to load requirements');
    } finally {
      setLoadingRequirements(false);
    }
  }, [projectId]);

  const fetchCandidates = useCallback(async () => {
    try {
      setLoadingCandidates(true);
      const data = await rfpService.getCandidates(projectId);
      setCandidates(data);
    } catch (error) {
      console.error('Error fetching candidates:', error);
      message.error('Failed to load vendor candidates');
    } finally {
      setLoadingCandidates(false);
    }
  }, [projectId]);

  const fetchClientVendors = useCallback(async () => {
    try {
      const data = await clientVendorService.getAll();
      setClientVendors(data);
    } catch (error) {
      console.error('Error fetching client vendors:', error);
    }
  }, []);

  const fetchSuggestions = useCallback(async () => {
    try {
      setLoadingSuggestions(true);
      const data = await rfpService.getSuggestions(projectId);
      setSuggestions(data.suggestions || []);
    } catch (error) {
      console.error('Error fetching suggestions:', error);
    } finally {
      setLoadingSuggestions(false);
    }
  }, [projectId]);

  const fetchScoringMatrix = useCallback(async () => {
    try {
      setLoadingScoring(true);
      const data = await rfpService.getScoringMatrix(projectId);
      setScoringMatrix(data);

      // Build a lookup of existing scores: { `${req_id}-${cand_id}`: score_value }
      const existingScores = {};
      if (data.scores) {
        data.scores.forEach((s) => {
          existingScores[`${s.requirement_id}-${s.candidate_id}`] = s.score;
        });
      }
      setScores(existingScores);
    } catch (error) {
      console.error('Error fetching scoring matrix:', error);
      message.error('Failed to load scoring matrix');
    } finally {
      setLoadingScoring(false);
    }
  }, [projectId]);

  const fetchResults = useCallback(async () => {
    try {
      setLoadingResults(true);
      const data = await rfpService.getResults(projectId);
      setResults(data);
    } catch (error) {
      console.error('Error fetching results:', error);
      message.error('Failed to load results');
    } finally {
      setLoadingResults(false);
    }
  }, [projectId]);

  // Initial load
  useEffect(() => {
    fetchProject();
    userService.getAll().then(setUsers).catch(() => {});
  }, [fetchProject]);

  // Load step-specific data when step changes
  useEffect(() => {
    if (!project) return;

    switch (currentStep) {
      case 1:
        fetchRequirements();
        break;
      case 2:
        fetchCandidates();
        fetchClientVendors();
        fetchSuggestions();
        break;
      case 3:
        fetchRequirements();
        fetchCandidates();
        fetchScoringMatrix();
        break;
      case 4:
        fetchResults();
        fetchCandidates();
        break;
      case 5:
        fetchCandidates();
        fetchResults();
        break;
      default:
        break;
    }
  }, [currentStep, project, fetchRequirements, fetchCandidates, fetchClientVendors, fetchScoringMatrix, fetchResults]);

  // -------------------------------------------------------------------
  // Step navigation
  // -------------------------------------------------------------------

  const handleNextStep = async () => {
    const nextStep = currentStep + 1;
    if (nextStep > 5) return;

    try {
      // Save setup form if on step 0
      if (currentStep === 0) {
        const values = await setupForm.validateFields();
        await rfpService.updateProject(projectId, values);
      }

      // Update project status for the new step
      const newStatus = STEP_TO_STATUS[nextStep];
      if (newStatus && newStatus !== project.status) {
        await rfpService.updateProject(projectId, { status: newStatus });
      }

      setCurrentStep(nextStep);
      await fetchProject();
      message.success('Moved to next step');
    } catch (error) {
      console.error('Error advancing step:', error);
      message.error('Failed to advance to next step');
    }
  };

  const handlePreviousStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  // -------------------------------------------------------------------
  // Step 0: Setup handlers
  // -------------------------------------------------------------------

  const handleBRDUpload = async () => {
    if (!brdFile) {
      message.warning('Please select a file first');
      return;
    }
    try {
      setUploading(true);
      await rfpService.uploadBRD(projectId, brdFile);
      message.success('BRD uploaded successfully');
      setBrdFile(null);
      await fetchProject();
    } catch (error) {
      console.error('Error uploading BRD:', error);
      message.error('Failed to upload BRD');
    } finally {
      setUploading(false);
    }
  };

  const handleExtractRequirements = async () => {
    try {
      setExtracting(true);
      message.loading({ content: 'Extracting requirements with AI...', key: 'extract', duration: 0 });
      const data = await rfpService.extractRequirements(projectId);
      setExtractedRequirements(data.requirements || data || []);
      message.success({ content: 'Requirements extracted successfully', key: 'extract' });

      // Move to requirements step to review
      setCurrentStep(1);
      await fetchProject();
    } catch (error) {
      console.error('Error extracting requirements:', error);
      message.error({ content: 'Failed to extract requirements', key: 'extract' });
    } finally {
      setExtracting(false);
    }
  };

  // -------------------------------------------------------------------
  // Step 1: Requirements handlers
  // -------------------------------------------------------------------

  const handleAddRequirement = async () => {
    try {
      const values = await addReqForm.validateFields();
      await rfpService.addRequirement(projectId, values);
      message.success('Requirement added');
      setAddReqModalVisible(false);
      addReqForm.resetFields();
      fetchRequirements();
    } catch (error) {
      console.error('Error adding requirement:', error);
      message.error('Failed to add requirement');
    }
  };

  const handleDeleteRequirement = async (reqId) => {
    try {
      await rfpService.deleteRequirement(reqId);
      message.success('Requirement removed');
      fetchRequirements();
    } catch (error) {
      console.error('Error deleting requirement:', error);
      message.error('Failed to delete requirement');
    }
  };

  const handleRequirementFieldChange = async (reqId, field, value) => {
    try {
      await rfpService.updateRequirement(reqId, { [field]: value });
      setRequirements((prev) =>
        prev.map((r) => (r.id === reqId ? { ...r, [field]: value } : r))
      );
    } catch (error) {
      console.error('Error updating requirement:', error);
      message.error('Failed to update requirement');
    }
  };

  const handleBulkAddExtracted = async () => {
    if (!extractedRequirements.length) return;
    try {
      setSavingRequirements(true);
      await rfpService.bulkAddRequirements(projectId, extractedRequirements);
      message.success(`${extractedRequirements.length} requirements added`);
      setExtractedRequirements([]);
      fetchRequirements();
    } catch (error) {
      console.error('Error bulk adding requirements:', error);
      message.error('Failed to save extracted requirements');
    } finally {
      setSavingRequirements(false);
    }
  };

  // -------------------------------------------------------------------
  // Step 2: Vendors handlers
  // -------------------------------------------------------------------

  const handleAddClientVendor = async () => {
    try {
      const values = await vendorForm.validateFields();
      await rfpService.addCandidate(projectId, {
        client_vendor_id: values.client_vendor_id,
      });
      message.success('Vendor candidate added');
      setAddVendorModalVisible(false);
      vendorForm.resetFields();
      fetchCandidates();
    } catch (error) {
      console.error('Error adding vendor candidate:', error);
      message.error('Failed to add vendor candidate');
    }
  };

  const handleAddExternalVendor = async () => {
    try {
      const values = await externalForm.validateFields();
      await rfpService.addCandidate(projectId, {
        external_vendor_name: values.vendor_name,
        external_vendor_website: values.vendor_website,
        external_vendor_contact: values.contact_email,
      });
      message.success('External vendor added');
      setAddExternalModalVisible(false);
      externalForm.resetFields();
      fetchCandidates();
    } catch (error) {
      console.error('Error adding external vendor:', error);
      message.error('Failed to add external vendor');
    }
  };

  const handleAcceptSuggestion = async (vendorId) => {
    try {
      await rfpService.addCandidate(projectId, { vendor_id: vendorId });
      message.success('Vendor added as candidate');
      setSuggestions((prev) => prev.filter((s) => s.vendor_id !== vendorId));
      fetchCandidates();
    } catch (error) {
      console.error('Error accepting suggestion:', error);
      message.error('Failed to add vendor');
    }
  };

  const handleDismissSuggestion = (vendorId) => {
    setDismissedSuggestions((prev) => new Set([...prev, vendorId]));
  };

  const handleDeleteCandidate = async (candidateId) => {
    try {
      await rfpService.deleteCandidate(candidateId);
      message.success('Vendor candidate removed');
      fetchCandidates();
    } catch (error) {
      console.error('Error deleting candidate:', error);
      message.error('Failed to remove vendor candidate');
    }
  };

  const handleAutoMatch = async () => {
    try {
      setAutoMatching(true);
      message.loading({ content: 'Running AI auto-match...', key: 'automatch', duration: 0 });
      await rfpService.autoMatch(projectId);
      message.success({ content: 'Auto-match complete', key: 'automatch' });
      fetchCandidates();
    } catch (error) {
      console.error('Error auto-matching:', error);
      message.error({ content: 'Auto-match failed', key: 'automatch' });
    } finally {
      setAutoMatching(false);
    }
  };

  // -------------------------------------------------------------------
  // Step 3: Scoring handlers
  // -------------------------------------------------------------------

  const handleScoreChange = async (requirementId, candidateId, value) => {
    const key = `${requirementId}-${candidateId}`;
    setScores((prev) => ({ ...prev, [key]: value }));

    try {
      await rfpService.submitScore(projectId, {
        requirement_id: requirementId,
        candidate_id: candidateId,
        score: value,
      });
    } catch (error) {
      console.error('Error submitting score:', error);
      message.error('Failed to save score');
    }
  };

  const handleBulkSubmitScores = async () => {
    const scoreEntries = Object.entries(scores).map(([key, score]) => {
      const [requirement_id, candidate_id] = key.split('-').map(Number);
      return { requirement_id, candidate_id, score };
    });

    if (scoreEntries.length === 0) {
      message.warning('No scores to submit');
      return;
    }

    try {
      await rfpService.bulkSubmitScores(projectId, scoreEntries);
      message.success('All scores submitted');
    } catch (error) {
      console.error('Error bulk submitting scores:', error);
      message.error('Failed to submit scores');
    }
  };

  // -------------------------------------------------------------------
  // Step 4: Results handler
  // -------------------------------------------------------------------

  const handleExportCSV = () => {
    const url = rfpService.exportCSV(projectId);
    const token = localStorage.getItem('token');
    window.open(`${url}?token=${token}`, '_blank');
  };

  // -------------------------------------------------------------------
  // Step 5: Decision handlers
  // -------------------------------------------------------------------

  const handleCloseProject = async () => {
    if (!selectedWinner) {
      message.warning('Please select a winning vendor');
      return;
    }

    Modal.confirm({
      title: 'Close RFP Project',
      content: 'Are you sure you want to close this project and finalize the decision? This cannot be undone.',
      okText: 'Close Project',
      okType: 'primary',
      onOk: async () => {
        try {
          setClosing(true);
          await rfpService.updateProject(projectId, {
            status: 'closed',
            selected_candidate_id: selectedWinner,
            decision_rationale: rationale,
          });
          message.success('Project closed successfully');
          await fetchProject();
        } catch (error) {
          console.error('Error closing project:', error);
          message.error('Failed to close project');
        } finally {
          setClosing(false);
        }
      },
    });
  };

  // -------------------------------------------------------------------
  // Render helpers
  // -------------------------------------------------------------------

  const renderSetupStep = () => (
    <Card title="Project Setup" className="rfp-step-card">
      <Form form={setupForm} layout="vertical" style={{ maxWidth: 700 }}>
        <Form.Item
          name="title"
          label="Project Title"
          rules={[{ required: true, message: 'Please enter a project title' }]}
        >
          <Input placeholder="e.g., CRM Platform RFP 2026" />
        </Form.Item>

        <Form.Item name="description" label="Description">
          <TextArea rows={4} placeholder="Describe the purpose and scope of this RFP..." />
        </Form.Item>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="rfp_type" label="RFP Type">
              <Select placeholder="Select type">
                <Option value="new_purchase">New Purchase</Option>
                <Option value="renewal">Renewal</Option>
                <Option value="replacement">Replacement</Option>
                <Option value="consolidation">Consolidation</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="deadline" label="Deadline">
              <Input type="date" />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item name="owner_user_id" label="Owner">
          <Select
            placeholder="Select project owner"
            allowClear
            showSearch
            optionFilterProp="children"
          >
            {users.map((u) => (
              <Option key={u.id} value={u.id}>
                {u.full_name || u.email}
              </Option>
            ))}
          </Select>
        </Form.Item>
      </Form>

      <Card
        type="inner"
        title="Upload Business Requirements Document (BRD)"
        style={{ marginTop: 24 }}
      >
        <Dragger
          beforeUpload={(file) => {
            setBrdFile(file);
            return false;
          }}
          maxCount={1}
          fileList={brdFile ? [brdFile] : []}
          onRemove={() => setBrdFile(null)}
          accept=".pdf,.docx,.doc,.txt,.md"
        >
          <p className="ant-upload-drag-icon">
            <UploadOutlined />
          </p>
          <p className="ant-upload-text">Click or drag BRD file to upload</p>
          <p className="ant-upload-hint">
            Supports PDF, DOCX, DOC, TXT, and Markdown files
          </p>
        </Dragger>

        <Space style={{ marginTop: 16 }}>
          <Button
            type="primary"
            icon={<UploadOutlined />}
            onClick={handleBRDUpload}
            loading={uploading}
            disabled={!brdFile}
          >
            Upload BRD
          </Button>
          <Button
            icon={<ThunderboltOutlined />}
            onClick={handleExtractRequirements}
            loading={extracting}
            disabled={!project?.brd_file_path}
            type="default"
          >
            Extract Requirements with AI
          </Button>
        </Space>

        {project?.brd_file_path && (
          <Alert
            message="BRD file uploaded"
            description={`File: ${project.brd_file_path}`}
            type="success"
            showIcon
            style={{ marginTop: 12 }}
          />
        )}
      </Card>
    </Card>
  );

  const renderRequirementsStep = () => {
    const reqColumns = [
      {
        title: 'Title',
        dataIndex: 'title',
        key: 'title',
        width: 200,
        render: (text, record) => (
          <Input
            defaultValue={text}
            onBlur={(e) => {
              if (e.target.value !== text) {
                handleRequirementFieldChange(record.id, 'title', e.target.value);
              }
            }}
            size="small"
          />
        ),
      },
      {
        title: 'Description',
        dataIndex: 'description',
        key: 'description',
        width: 300,
        render: (text, record) => (
          <Input.TextArea
            defaultValue={text}
            autoSize={{ minRows: 1, maxRows: 3 }}
            onBlur={(e) => {
              if (e.target.value !== text) {
                handleRequirementFieldChange(record.id, 'description', e.target.value);
              }
            }}
            size="small"
          />
        ),
      },
      {
        title: 'Category',
        dataIndex: 'category',
        key: 'category',
        width: 140,
        render: (text, record) => (
          <Input
            defaultValue={text}
            onBlur={(e) => {
              if (e.target.value !== text) {
                handleRequirementFieldChange(record.id, 'category', e.target.value);
              }
            }}
            size="small"
          />
        ),
      },
      {
        title: 'Priority',
        dataIndex: 'priority',
        key: 'priority',
        width: 120,
        render: (priority, record) => (
          <Select
            defaultValue={priority || 'medium'}
            onChange={(val) => handleRequirementFieldChange(record.id, 'priority', val)}
            size="small"
            style={{ width: '100%' }}
          >
            <Option value="low"><Tag color="default">Low</Tag></Option>
            <Option value="medium"><Tag color="blue">Medium</Tag></Option>
            <Option value="high"><Tag color="orange">High</Tag></Option>
            <Option value="critical"><Tag color="red">Critical</Tag></Option>
          </Select>
        ),
      },
      {
        title: 'Weight',
        dataIndex: 'weight',
        key: 'weight',
        width: 80,
        render: (weight, record) => (
          <InputNumber
            defaultValue={weight || 1}
            min={0}
            max={100}
            size="small"
            style={{ width: '100%' }}
            onBlur={(e) => {
              const val = parseFloat(e.target.value);
              if (!isNaN(val) && val !== weight) {
                handleRequirementFieldChange(record.id, 'weight', val);
              }
            }}
          />
        ),
      },
      {
        title: 'Mandatory',
        dataIndex: 'is_mandatory',
        key: 'is_mandatory',
        width: 80,
        align: 'center',
        render: (val, record) => (
          <Checkbox
            defaultChecked={val}
            onChange={(e) =>
              handleRequirementFieldChange(record.id, 'is_mandatory', e.target.checked)
            }
          />
        ),
      },
      {
        title: 'Action',
        key: 'action',
        width: 60,
        align: 'center',
        render: (_, record) => (
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            size="small"
            onClick={() => handleDeleteRequirement(record.id)}
          />
        ),
      },
    ];

    return (
      <Card
        title={`Requirements (${requirements.length})`}
        className="rfp-step-card"
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setAddReqModalVisible(true)}
          >
            Add Requirement
          </Button>
        }
      >
        {/* Extracted requirements alert */}
        {extractedRequirements.length > 0 && (
          <Alert
            message={`${extractedRequirements.length} requirements extracted by AI`}
            description="Review the extracted requirements below and click 'Confirm & Save' to add them to the project."
            type="info"
            showIcon
            icon={<ThunderboltOutlined />}
            style={{ marginBottom: 16 }}
            action={
              <Button
                type="primary"
                onClick={handleBulkAddExtracted}
                loading={savingRequirements}
              >
                Confirm & Save
              </Button>
            }
          />
        )}

        {extractedRequirements.length > 0 && (
          <Card type="inner" title="Extracted Requirements Preview" style={{ marginBottom: 16 }}>
            <Table
              dataSource={extractedRequirements.map((r, idx) => ({ ...r, _key: idx }))}
              rowKey="_key"
              size="small"
              pagination={false}
              columns={[
                { title: 'Title', dataIndex: 'title', key: 'title' },
                { title: 'Description', dataIndex: 'description', key: 'description', ellipsis: true },
                { title: 'Category', dataIndex: 'category', key: 'category' },
                {
                  title: 'Priority',
                  dataIndex: 'priority',
                  key: 'priority',
                  render: (p) => <Tag color={PRIORITY_COLORS[p] || 'default'}>{p || 'medium'}</Tag>,
                },
              ]}
            />
          </Card>
        )}

        <Spin spinning={loadingRequirements}>
          {requirements.length > 0 ? (
            <Table
              dataSource={requirements}
              columns={reqColumns}
              rowKey="id"
              size="small"
              pagination={{ pageSize: 20 }}
              scroll={{ x: 980 }}
            />
          ) : (
            <Empty description="No requirements yet. Add manually or extract from BRD." />
          )}
        </Spin>

        {/* Add requirement modal */}
        <Modal
          title="Add Requirement"
          open={addReqModalVisible}
          onOk={handleAddRequirement}
          onCancel={() => {
            setAddReqModalVisible(false);
            addReqForm.resetFields();
          }}
          width={600}
        >
          <Form form={addReqForm} layout="vertical">
            <Form.Item
              name="title"
              label="Title"
              rules={[{ required: true, message: 'Enter requirement title' }]}
            >
              <Input placeholder="e.g., Single Sign-On Support" />
            </Form.Item>
            <Form.Item name="description" label="Description">
              <TextArea rows={3} placeholder="Detailed description..." />
            </Form.Item>
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item name="category" label="Category">
                  <Input placeholder="e.g., Security" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="priority" label="Priority" initialValue="medium">
                  <Select>
                    <Option value="low">Low</Option>
                    <Option value="medium">Medium</Option>
                    <Option value="high">High</Option>
                    <Option value="critical">Critical</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="weight" label="Weight" initialValue={1}>
                  <InputNumber min={0} max={100} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item name="is_mandatory" valuePropName="checked" initialValue={false}>
              <Checkbox>Mandatory requirement</Checkbox>
            </Form.Item>
          </Form>
        </Modal>
      </Card>
    );
  };

  const renderVendorsStep = () => {
    const candidateColumns = [
      {
        title: 'Vendor Name',
        dataIndex: 'display_name',
        key: 'display_name',
      },
      {
        title: 'Source',
        key: 'source',
        width: 120,
        render: (_, record) =>
          record.client_vendor_id ? (
            <Tag color="blue">Internal</Tag>
          ) : (
            <Tag color="purple">External</Tag>
          ),
      },
      {
        title: 'Website',
        dataIndex: 'vendor_website',
        key: 'vendor_website',
        width: 200,
        render: (url) =>
          url ? (
            <a href={url} target="_blank" rel="noopener noreferrer">
              {url}
            </a>
          ) : (
            <Text type="secondary">-</Text>
          ),
      },
      {
        title: 'AI Match Score',
        dataIndex: 'ai_match_score',
        key: 'ai_match_score',
        width: 130,
        sorter: (a, b) => (a.ai_match_score || 0) - (b.ai_match_score || 0),
        render: (score) =>
          score != null ? (
            <Tag color={score >= 80 ? 'green' : score >= 60 ? 'blue' : 'orange'}>
              {score.toFixed(1)}%
            </Tag>
          ) : (
            <Text type="secondary">-</Text>
          ),
      },
      {
        title: 'Contact',
        key: 'contact',
        width: 160,
        render: (_, record) =>
          record.contact_name || record.contact_email ? (
            <Space direction="vertical" size={0}>
              {record.contact_name && <Text>{record.contact_name}</Text>}
              {record.contact_email && (
                <Text type="secondary" style={{ fontSize: 12 }}>{record.contact_email}</Text>
              )}
            </Space>
          ) : (
            <Text type="secondary">-</Text>
          ),
      },
      {
        title: 'Action',
        key: 'action',
        width: 60,
        align: 'center',
        render: (_, record) => (
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            size="small"
            onClick={() => handleDeleteCandidate(record.id)}
          />
        ),
      },
    ];

    return (
      <Card
        title={`Vendor Candidates (${candidates.length})`}
        className="rfp-step-card"
        extra={
          <Space>
            <Button
              icon={<ThunderboltOutlined />}
              onClick={handleAutoMatch}
              loading={autoMatching}
            >
              AI Auto-Match
            </Button>
            <Button
              icon={<PlusOutlined />}
              onClick={() => setAddVendorModalVisible(true)}
            >
              Add from My Vendors
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setAddExternalModalVisible(true)}
            >
              Add External Vendor
            </Button>
          </Space>
        }
      >
        <Spin spinning={loadingCandidates}>
          {candidates.length > 0 ? (
            <Table
              dataSource={candidates}
              columns={candidateColumns}
              rowKey="id"
              size="small"
              pagination={{ pageSize: 20 }}
            />
          ) : (
            <Empty description="No vendor candidates yet. Add vendors from your list or add external vendors." />
          )}
        </Spin>

        {/* AI Suggestions */}
        <Divider />
        <Card
          type="inner"
          title={
            <Space>
              <BulbOutlined />
              <span>AI Suggested Vendors ({suggestions.filter((s) => !dismissedSuggestions.has(s.vendor_id)).length})</span>
            </Space>
          }
          extra={
            <Button
              icon={<ReloadOutlined />}
              size="small"
              onClick={fetchSuggestions}
              loading={loadingSuggestions}
            >
              Refresh
            </Button>
          }
        >
          {loadingSuggestions ? (
            <div style={{ textAlign: 'center', padding: 24 }}>
              <Spin tip="Analyzing vendor capabilities..." />
            </div>
          ) : suggestions.filter((s) => !dismissedSuggestions.has(s.vendor_id)).length > 0 ? (
            <Table
              dataSource={suggestions.filter((s) => !dismissedSuggestions.has(s.vendor_id))}
              rowKey="vendor_id"
              size="small"
              pagination={false}
              columns={[
                {
                  title: 'Vendor',
                  dataIndex: 'vendor_name',
                  key: 'vendor_name',
                },
                {
                  title: 'Products',
                  dataIndex: 'product_count',
                  key: 'product_count',
                  width: 90,
                  align: 'center',
                },
                {
                  title: 'Source',
                  key: 'source',
                  width: 100,
                  render: (_, record) =>
                    record.is_internal ? (
                      <Tag color="blue">Internal</Tag>
                    ) : (
                      <Tag>External</Tag>
                    ),
                },
                {
                  title: 'Match Score',
                  dataIndex: 'ai_match_score',
                  key: 'ai_match_score',
                  width: 120,
                  sorter: (a, b) => a.ai_match_score - b.ai_match_score,
                  defaultSortOrder: 'descend',
                  render: (score) => (
                    <Tag color={score >= 70 ? 'green' : score >= 50 ? 'blue' : 'orange'}>
                      {score.toFixed(1)}%
                    </Tag>
                  ),
                },
                {
                  title: 'Top Matches',
                  key: 'top_matches',
                  ellipsis: true,
                  render: (_, record) => {
                    const matches = record.top_matches || [];
                    if (matches.length === 0) return <Text type="secondary">-</Text>;
                    return (
                      <Tooltip
                        title={matches.map((m) => `${m.requirement_title}: ${m.match_score}%`).join('\n')}
                      >
                        <Space size={4} wrap>
                          {matches.map((m, i) => (
                            <Tag key={i} style={{ fontSize: 11 }}>
                              {m.requirement_title.length > 25
                                ? m.requirement_title.slice(0, 25) + '...'
                                : m.requirement_title}
                            </Tag>
                          ))}
                        </Space>
                      </Tooltip>
                    );
                  },
                },
                {
                  title: 'Actions',
                  key: 'actions',
                  width: 140,
                  align: 'center',
                  render: (_, record) => (
                    <Space size="small">
                      <Button
                        type="primary"
                        size="small"
                        icon={<CheckOutlined />}
                        onClick={() => handleAcceptSuggestion(record.vendor_id)}
                      >
                        Add
                      </Button>
                      <Button
                        size="small"
                        icon={<CloseOutlined />}
                        onClick={() => handleDismissSuggestion(record.vendor_id)}
                      />
                    </Space>
                  ),
                },
              ]}
            />
          ) : (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                requirements.length === 0
                  ? 'Add requirements first to get AI vendor suggestions.'
                  : 'No additional vendor suggestions available.'
              }
            />
          )}
        </Card>

        {/* Add from client vendors modal */}
        <Modal
          title="Add Vendor from Your Organization"
          open={addVendorModalVisible}
          onOk={handleAddClientVendor}
          onCancel={() => {
            setAddVendorModalVisible(false);
            vendorForm.resetFields();
          }}
        >
          <Form form={vendorForm} layout="vertical">
            <Form.Item
              name="client_vendor_id"
              label="Select Vendor"
              rules={[{ required: true, message: 'Please select a vendor' }]}
            >
              <Select
                placeholder="Search and select a vendor..."
                showSearch
                optionFilterProp="children"
                style={{ width: '100%' }}
              >
                {clientVendors.map((cv) => (
                  <Option key={cv.id} value={cv.id}>
                    {cv.vendor?.name || cv.display_name || `Vendor ${cv.vendor_id}`}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Form>
        </Modal>

        {/* Add external vendor modal */}
        <Modal
          title="Add External Vendor"
          open={addExternalModalVisible}
          onOk={handleAddExternalVendor}
          onCancel={() => {
            setAddExternalModalVisible(false);
            externalForm.resetFields();
          }}
          width={500}
        >
          <Form form={externalForm} layout="vertical">
            <Form.Item
              name="vendor_name"
              label="Vendor Name"
              rules={[{ required: true, message: 'Please enter vendor name' }]}
            >
              <Input placeholder="e.g., Acme Corp" />
            </Form.Item>
            <Form.Item name="vendor_website" label="Website">
              <Input placeholder="https://www.example.com" />
            </Form.Item>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="contact_name" label="Contact Name">
                  <Input placeholder="John Doe" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="contact_email" label="Contact Email">
                  <Input placeholder="john@example.com" />
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Modal>
      </Card>
    );
  };

  const renderScoringStep = () => {
    if (loadingScoring) {
      return (
        <Card className="rfp-step-card">
          <div style={{ textAlign: 'center', padding: 50 }}>
            <Spin size="large" />
            <div style={{ marginTop: 16 }}>Loading scoring matrix...</div>
          </div>
        </Card>
      );
    }

    // Group requirements by category
    const reqsByCategory = {};
    (requirements || []).forEach((req) => {
      const cat = req.category || 'Uncategorized';
      if (!reqsByCategory[cat]) {
        reqsByCategory[cat] = [];
      }
      reqsByCategory[cat].push(req);
    });

    // Build flat data with category grouping
    const tableData = [];
    Object.entries(reqsByCategory).forEach(([category, reqs]) => {
      reqs.forEach((req, idx) => {
        tableData.push({
          ...req,
          _category: category,
          _isFirstInCategory: idx === 0,
          _categoryCount: reqs.length,
        });
      });
    });

    const scoringScale = project?.scoring_scale || 5;
    const isNumberMode = scoringInputMode === 'number';
    const vendorColWidth = isNumberMode ? 90 : 160;

    // Build columns: requirement info + one col per candidate
    const scoringColumns = [
      {
        title: 'Requirement',
        key: 'requirement',
        width: 260,
        fixed: 'left',
        render: (_, record) => (
          <div style={{ lineHeight: 1.3 }}>
            <Space size={4}>
              <Text strong style={{ fontSize: 13 }}>{record.title}</Text>
              {record.is_mandatory && (
                <Tag color="red" style={{ fontSize: 10, lineHeight: '16px', padding: '0 4px' }}>REQ</Tag>
              )}
            </Space>
            <div>
              <Tag color="geekblue" style={{ fontSize: 10, lineHeight: '16px', padding: '0 4px', marginTop: 2 }}>{record._category}</Tag>
            </div>
          </div>
        ),
      },
      ...candidates.map((cand, candIdx) => ({
        title: (
          <Tooltip title={cand.display_name || `Vendor #${cand.id}`}>
            <Text strong style={{ fontSize: 11, display: 'block', textAlign: 'center', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: vendorColWidth - 16 }}>
              {cand.display_name || `Vendor #${cand.id}`}
            </Text>
          </Tooltip>
        ),
        key: `cand-${cand.id}`,
        width: vendorColWidth,
        align: 'center',
        render: (_, record, rowIdx) => {
          const scoreKey = `${record.id}-${cand.id}`;
          if (isNumberMode) {
            return (
              <InputNumber
                min={0}
                max={scoringScale}
                value={scores[scoreKey] || null}
                placeholder="–"
                size="small"
                onChange={(value) => handleScoreChange(record.id, cand.id, value || 0)}
                style={{ width: 56, textAlign: 'center' }}
                tabIndex={rowIdx * candidates.length + candIdx + 1}
              />
            );
          }
          return (
            <Rate
              count={scoringScale}
              value={scores[scoreKey] || 0}
              onChange={(value) => handleScoreChange(record.id, cand.id, value)}
              style={{ fontSize: 14 }}
            />
          );
        },
      })),
    ];

    return (
      <Card
        title={
          <Space>
            <StarOutlined />
            <span>Capability Scoring Matrix</span>
          </Space>
        }
        className="rfp-step-card"
        extra={
          <Space>
            <Button.Group size="small">
              <Tooltip title="Number input (keyboard friendly)">
                <Button
                  type={isNumberMode ? 'primary' : 'default'}
                  icon={<NumberOutlined />}
                  onClick={() => setScoringInputMode('number')}
                />
              </Tooltip>
              <Tooltip title="Star rating">
                <Button
                  type={!isNumberMode ? 'primary' : 'default'}
                  icon={<StarOutlined />}
                  onClick={() => setScoringInputMode('stars')}
                />
              </Tooltip>
            </Button.Group>
            <Button type="primary" onClick={handleBulkSubmitScores}>
              Submit All Scores
            </Button>
          </Space>
        }
      >
        {requirements.length === 0 || candidates.length === 0 ? (
          <Empty description="You need both requirements and vendor candidates before scoring." />
        ) : (
          <Table
            dataSource={tableData}
            columns={scoringColumns}
            rowKey="id"
            size="small"
            pagination={false}
            scroll={{ x: 260 + candidates.length * vendorColWidth, y: 600 }}
            bordered
          />
        )}
      </Card>
    );
  };

  const renderResultsStep = () => {
    if (loadingResults) {
      return (
        <Card className="rfp-step-card">
          <div style={{ textAlign: 'center', padding: 50 }}>
            <Spin size="large" />
            <div style={{ marginTop: 16 }}>Calculating results...</div>
          </div>
        </Card>
      );
    }

    const rankings = results?.rankings || results || [];

    const resultColumns = [
      {
        title: 'Rank',
        key: 'rank',
        width: 70,
        render: (_, __, index) => {
          const rank = index + 1;
          return (
            <Space>
              {rank <= 3 && (
                <TrophyOutlined
                  style={{
                    color: rank === 1 ? '#ffd700' : rank === 2 ? '#c0c0c0' : '#cd7f32',
                  }}
                />
              )}
              <Text strong>#{rank}</Text>
            </Space>
          );
        },
      },
      {
        title: 'Vendor',
        dataIndex: 'candidate_name',
        key: 'candidate_name',
      },
      {
        title: 'Weighted Total',
        dataIndex: 'weighted_total',
        key: 'weighted_total',
        width: 140,
        sorter: (a, b) => (a.weighted_total || 0) - (b.weighted_total || 0),
        defaultSortOrder: 'descend',
        render: (val) => (
          <Tag
            color={val >= 80 ? 'green' : val >= 60 ? 'blue' : val >= 40 ? 'orange' : 'red'}
            style={{ fontSize: 14, padding: '2px 10px' }}
          >
            {val != null ? val.toFixed(2) : '-'}
          </Tag>
        ),
      },
      {
        title: 'Mandatory Met',
        dataIndex: 'mandatory_met',
        key: 'mandatory_met',
        width: 120,
        align: 'center',
        render: (val) =>
          val === true || val === 'yes' ? (
            <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 18 }} />
          ) : val === false || val === 'no' ? (
            <CloseCircleOutlined style={{ color: '#ff4d4f', fontSize: 18 }} />
          ) : (
            <Text type="secondary">-</Text>
          ),
      },
      {
        title: 'Vendor Self Score',
        dataIndex: 'vendor_self_total',
        key: 'vendor_self_total',
        width: 140,
        render: (val) =>
          val != null ? <Text>{val.toFixed(2)}</Text> : <Text type="secondary">-</Text>,
      },
      {
        title: 'Consensus',
        dataIndex: 'evaluator_consensus',
        key: 'evaluator_consensus',
        width: 120,
        render: (val) =>
          val != null ? <Text>{val.toFixed(2)}</Text> : <Text type="secondary">-</Text>,
      },
    ];

    return (
      <Card
        title={
          <Space>
            <TrophyOutlined />
            <span>Evaluation Results</span>
          </Space>
        }
        className="rfp-step-card"
        extra={
          <Button icon={<DownloadOutlined />} onClick={handleExportCSV}>
            Export CSV
          </Button>
        }
      >
        {rankings.length > 0 ? (
          <>
            <Row gutter={16} style={{ marginBottom: 24 }}>
              <Col span={6}>
                <Statistic
                  title="Total Candidates"
                  value={rankings.length}
                  prefix={<FileSearchOutlined />}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title="Top Score"
                  value={rankings.length > 0 ? (rankings[0].weighted_total || 0).toFixed(1) : '-'}
                  prefix={<TrophyOutlined style={{ color: '#ffd700' }} />}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title="Requirements Scored"
                  value={requirements?.length || results?.total_requirements || '-'}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title="Leader"
                  value={
                    rankings.length > 0
                      ? rankings[0].candidate_name || '-'
                      : '-'
                  }
                  valueStyle={{ fontSize: 16 }}
                />
              </Col>
            </Row>
            <Table
              dataSource={rankings}
              columns={resultColumns}
              rowKey={(record) => record.candidate_id || record.id}
              size="middle"
              pagination={false}
            />
          </>
        ) : (
          <Empty description="No results available yet. Complete scoring first." />
        )}
      </Card>
    );
  };

  const renderDecisionStep = () => {
    const isAlreadyClosed = project?.status === 'closed';

    return (
      <Card
        title={
          <Space>
            <CheckCircleOutlined />
            <span>Final Decision</span>
          </Space>
        }
        className="rfp-step-card"
      >
        {isAlreadyClosed && (
          <Alert
            message="This project has been closed"
            description={
              project.decision_rationale
                ? `Rationale: ${project.decision_rationale}`
                : 'The vendor selection has been finalized.'
            }
            type="success"
            showIcon
            style={{ marginBottom: 24 }}
          />
        )}

        <Descriptions
          bordered
          column={1}
          style={{ marginBottom: 24 }}
          size="small"
        >
          <Descriptions.Item label="Project">
            {project?.title}
          </Descriptions.Item>
          <Descriptions.Item label="Status">
            <Tag color={isAlreadyClosed ? 'green' : 'processing'}>
              {project?.status?.toUpperCase()}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Type">
            {project?.rfp_type || '-'}
          </Descriptions.Item>
          <Descriptions.Item label="Deadline">
            {project?.deadline || '-'}
          </Descriptions.Item>
        </Descriptions>

        <div style={{ marginBottom: 24 }}>
          <Text strong style={{ display: 'block', marginBottom: 8, fontSize: 16 }}>
            Select Winning Vendor
          </Text>
          <Select
            placeholder="Choose the winning vendor..."
            value={selectedWinner}
            onChange={setSelectedWinner}
            style={{ width: '100%', maxWidth: 500 }}
            disabled={isAlreadyClosed}
            size="large"
          >
            {candidates.map((cand) => (
              <Option key={cand.id} value={cand.id}>
                {cand.display_name || `Candidate #${cand.id}`}
              </Option>
            ))}
          </Select>
        </div>

        <div style={{ marginBottom: 24 }}>
          <Text strong style={{ display: 'block', marginBottom: 8, fontSize: 16 }}>
            Decision Rationale
          </Text>
          <TextArea
            rows={5}
            value={rationale}
            onChange={(e) => setRationale(e.target.value)}
            placeholder="Explain why this vendor was selected..."
            disabled={isAlreadyClosed}
            style={{ maxWidth: 600 }}
          />
        </div>

        {!isAlreadyClosed && (
          <Button
            type="primary"
            size="large"
            icon={<CheckCircleOutlined />}
            onClick={handleCloseProject}
            loading={closing}
            danger
          >
            Close Project
          </Button>
        )}
      </Card>
    );
  };

  // -------------------------------------------------------------------
  // Main render
  // -------------------------------------------------------------------

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 80 }}>
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>Loading RFP project...</div>
      </div>
    );
  }

  if (!project) {
    return (
      <div style={{ textAlign: 'center', padding: 80 }}>
        <Empty description="RFP project not found" />
      </div>
    );
  }

  const stepItems = [
    { title: 'Setup', icon: <FileSearchOutlined /> },
    { title: 'Requirements', icon: <FileSearchOutlined /> },
    { title: 'Vendors', icon: <PlusOutlined /> },
    { title: 'Score', icon: <StarOutlined /> },
    { title: 'Results', icon: <TrophyOutlined /> },
    { title: 'Decision', icon: <CheckCircleOutlined /> },
  ];

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 0:
        return renderSetupStep();
      case 1:
        return renderRequirementsStep();
      case 2:
        return renderVendorsStep();
      case 3:
        return renderScoringStep();
      case 4:
        return renderResultsStep();
      case 5:
        return renderDecisionStep();
      default:
        return null;
    }
  };

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <Button
            type="link"
            icon={<LeftOutlined />}
            onClick={() => navigate('/rfp')}
            style={{ padding: 0, marginBottom: 4, fontSize: 13 }}
          >
            All RFP Projects
          </Button>
          <Title level={2} style={{ marginBottom: 4 }}>
            {project.title || 'RFP Project'}
          </Title>
          <Space>
            <Tag color="processing">{project.status}</Tag>
            {project.rfp_type && <Tag>{project.rfp_type}</Tag>}
            {project.deadline && (
              <Text type="secondary">Deadline: {project.deadline}</Text>
            )}
          </Space>
        </div>
        <Space>
          <Button
            icon={<LeftOutlined />}
            onClick={handlePreviousStep}
            disabled={currentStep === 0}
          >
            Back
          </Button>
          <Button
            type="primary"
            icon={<RightOutlined />}
            onClick={handleNextStep}
            disabled={currentStep === 5 || project.status === 'closed'}
          >
            Next
          </Button>
        </Space>
      </div>

      {/* Steps wizard */}
      <Steps
        current={currentStep}
        items={stepItems}
        style={{ marginBottom: 32 }}
        onChange={(step) => setCurrentStep(step)}
      />

      {/* Step content */}
      {renderCurrentStep()}

      {/* Navigation buttons */}
      <div
        style={{
          marginTop: 24,
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        <Button
          size="large"
          onClick={handlePreviousStep}
          disabled={currentStep === 0}
        >
          Previous Step
        </Button>
        <Button
          type="primary"
          size="large"
          onClick={handleNextStep}
          disabled={currentStep === 5 || project.status === 'closed'}
        >
          Next Step
        </Button>
      </div>
    </div>
  );
};

export default RFPProjectDetail;
